/**
 * API client for Rufus Spaces backend
 * Uses fetch API for HTTP requests
 */

import {
  Space,
  SpaceFile,
  FileVersion,
  CreateSpaceInput,
  UpdateSpaceInput,
  SaveFileInput,
  SpacesListResponse,
  FilesListResponse,
  FileVersionsResponse,
  FileContentResponse,
  ApiError,
  Template,
  TemplateSummary,
  Collaborator,
  AddCollaboratorInput,
  UpdateCollaboratorInput,
  CollaboratorsListResponse,
} from '../types';

// API base URL - configurable via environment
const API_BASE_URL = process.env.API_BASE_URL || '/api';

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  public readonly status: number;
  public readonly details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultHeaders: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const response = await fetch(url, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorBody: ApiError | undefined;
    try {
      errorBody = await response.json();
    } catch {
      // Response body is not JSON
    }

    throw new ApiClientError(
      errorBody?.error || `HTTP ${response.status}: ${response.statusText}`,
      response.status,
      errorBody?.details
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Spaces API
 */
export const spacesApi = {
  /**
   * List all spaces for the current user
   */
  async list(options?: {
    limit?: number;
    cursor?: string;
  }): Promise<SpacesListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);

    const query = params.toString();
    const endpoint = query ? `/spaces?${query}` : '/spaces';

    return fetchApi<SpacesListResponse>(endpoint);
  },

  /**
   * Get a single space by ID
   */
  async get(spaceId: string): Promise<Space> {
    return fetchApi<Space>(`/spaces/${encodeURIComponent(spaceId)}`);
  },

  /**
   * Create a new space
   */
  async create(input: CreateSpaceInput): Promise<Space> {
    return fetchApi<Space>('/spaces', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  /**
   * Update an existing space
   */
  async update(spaceId: string, input: UpdateSpaceInput): Promise<Space> {
    return fetchApi<Space>(`/spaces/${encodeURIComponent(spaceId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },

  /**
   * Delete a space
   */
  async delete(spaceId: string): Promise<void> {
    return fetchApi<void>(`/spaces/${encodeURIComponent(spaceId)}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Files API
 */
export const filesApi = {
  /**
   * List all files in a space
   */
  async list(spaceId: string): Promise<FilesListResponse> {
    return fetchApi<FilesListResponse>(
      `/files/${encodeURIComponent(spaceId)}`
    );
  },

  /**
   * Get file content
   */
  async get(
    spaceId: string,
    filePath: string,
    versionId?: string
  ): Promise<FileContentResponse> {
    const params = new URLSearchParams();
    if (versionId) params.set('versionId', versionId);

    const query = params.toString();
    const endpoint = `/files/${encodeURIComponent(spaceId)}/${encodeURIComponent(filePath)}${query ? `?${query}` : ''}`;

    return fetchApi<FileContentResponse>(endpoint);
  },

  /**
   * Save (create or update) a file
   */
  async save(
    spaceId: string,
    filePath: string,
    input: SaveFileInput
  ): Promise<SpaceFile> {
    return fetchApi<SpaceFile>(
      `/files/${encodeURIComponent(spaceId)}/${encodeURIComponent(filePath)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
  },

  /**
   * Delete a file
   */
  async delete(spaceId: string, filePath: string): Promise<void> {
    return fetchApi<void>(
      `/files/${encodeURIComponent(spaceId)}/${encodeURIComponent(filePath)}`,
      {
        method: 'DELETE',
      }
    );
  },

  /**
   * Get file version history
   */
  async versions(
    spaceId: string,
    filePath: string,
    options?: { limit?: number; cursor?: string }
  ): Promise<FileVersionsResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);

    const query = params.toString();
    const endpoint = `/files/${encodeURIComponent(spaceId)}/${encodeURIComponent(filePath)}/versions${query ? `?${query}` : ''}`;

    return fetchApi<FileVersionsResponse>(endpoint);
  },

  /**
   * Restore a file to a previous version
   */
  async restore(
    spaceId: string,
    filePath: string,
    versionId: string
  ): Promise<SpaceFile> {
    return fetchApi<SpaceFile>(
      `/files/${encodeURIComponent(spaceId)}/${encodeURIComponent(filePath)}/restore`,
      {
        method: 'POST',
        body: JSON.stringify({ versionId }),
      }
    );
  },
};

/**
 * Deploy API
 */
export const deployApi = {
  /**
   * Trigger a deployment
   */
  async trigger(spaceId: string): Promise<{ deploymentId: string }> {
    return fetchApi<{ deploymentId: string }>(
      `/deploy/${encodeURIComponent(spaceId)}/trigger`,
      {
        method: 'POST',
      }
    );
  },

  /**
   * Get deployment status
   */
  async status(spaceId: string): Promise<{
    status: string;
    deploymentId?: string;
    url?: string;
  }> {
    return fetchApi<{ status: string; deploymentId?: string; url?: string }>(
      `/deploy/${encodeURIComponent(spaceId)}/status`
    );
  },

  /**
   * Get deployment logs
   */
  async logs(
    spaceId: string,
    options?: { deploymentId?: string; since?: string }
  ): Promise<{ logs: string[] }> {
    const params = new URLSearchParams();
    if (options?.deploymentId) params.set('deploymentId', options.deploymentId);
    if (options?.since) params.set('since', options.since);

    const query = params.toString();
    const endpoint = `/deploy/${encodeURIComponent(spaceId)}/logs${query ? `?${query}` : ''}`;

    return fetchApi<{ logs: string[] }>(endpoint);
  },

  /**
   * Stop a running space
   */
  async stop(spaceId: string): Promise<void> {
    return fetchApi<void>(`/deploy/${encodeURIComponent(spaceId)}/stop`, {
      method: 'POST',
    });
  },

  /**
   * Start a stopped space
   */
  async start(spaceId: string): Promise<void> {
    return fetchApi<void>(`/deploy/${encodeURIComponent(spaceId)}/start`, {
      method: 'POST',
    });
  },
};

/**
 * Templates API
 */
export const templatesApi = {
  /**
   * List all available templates (without file contents)
   */
  async list(): Promise<TemplateSummary[]> {
    return fetchApi<TemplateSummary[]>('/templates');
  },

  /**
   * Get a specific template with full file contents
   */
  async get(templateId: string): Promise<Template> {
    return fetchApi<Template>(`/templates/${encodeURIComponent(templateId)}`);
  },
};

/**
 * Collaborators API
 */
export const collaboratorsApi = {
  /**
   * List all collaborators for a space
   */
  async list(spaceId: string): Promise<CollaboratorsListResponse> {
    return fetchApi<CollaboratorsListResponse>(
      `/collaborators/${encodeURIComponent(spaceId)}`
    );
  },

  /**
   * Get a specific collaborator
   */
  async get(spaceId: string, userId: string): Promise<Collaborator> {
    return fetchApi<Collaborator>(
      `/collaborators/${encodeURIComponent(spaceId)}/${encodeURIComponent(userId)}`
    );
  },

  /**
   * Add a collaborator to a space
   */
  async add(spaceId: string, input: AddCollaboratorInput): Promise<Collaborator> {
    return fetchApi<Collaborator>(
      `/collaborators/${encodeURIComponent(spaceId)}`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },

  /**
   * Update a collaborator's role
   */
  async update(
    spaceId: string,
    userId: string,
    input: UpdateCollaboratorInput
  ): Promise<Collaborator> {
    return fetchApi<Collaborator>(
      `/collaborators/${encodeURIComponent(spaceId)}/${encodeURIComponent(userId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
  },

  /**
   * Remove a collaborator from a space
   */
  async remove(spaceId: string, userId: string): Promise<void> {
    return fetchApi<void>(
      `/collaborators/${encodeURIComponent(spaceId)}/${encodeURIComponent(userId)}`,
      {
        method: 'DELETE',
      }
    );
  },
};

// Export all APIs as a single object
export const api = {
  spaces: spacesApi,
  files: filesApi,
  deploy: deployApi,
  templates: templatesApi,
  collaborators: collaboratorsApi,
};

export default api;
